import { Fragment, useMemo, useState } from 'react';
import { LineChart } from '../charts/LineChart';
import { ChartCard, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

/** One managed-service line on one contract, as it bills today. */
interface Line {
  id: number;
  item: string;
  period: string | null;
  perYear: number;
  units: number | null;
  unitPrice: number | null;
  unitCost: number | null;
  annual: number;
  annualCost: number;
}
interface Contract {
  id: number;
  name: string;
  number: string | null;
  type: string | null;
  start: string | null;
  end: string | null;
  annual: number;
  annualCost: number;
  lines: Line[];
}
interface Client {
  id: number;
  name: string;
  annual: number;
  annualCost: number;
  contracts: Contract[];
}
interface ItemTotal {
  item: string;
  lines: number;
  clients: number;
  units: number;
  annual: number;
  annualCost: number;
  minPrice: number | null;
  maxPrice: number | null;
}
interface Payload {
  fetchedUtc: string;
  items: string[];
  byItem: ItemTotal[];
  /** Items nothing on any active contract matched — a renamed service, probably. */
  missing: string[];
  /** Services that look like one of these and did not match, so a drift shows. */
  nearMisses: { name: string; lines: number }[];
  activeContracts: number;
  contractsWithItems: number;
  activeClients: number;
  /** False when the Autotask user cannot read ContractServiceUnits. */
  unitsKnown: boolean;
  periodUnknown: number;
  oneTime: number;
  inactiveLines: number;
  unpriced: number;
  annual: number;
  annualCost: number;
  clients: Client[];
}

const YEARS = [1, 2, 3, 4, 5] as const;
const money = (v: number) => formatNumber(v, 'currency');
const money0 = (v: number) => v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyShort = (v: number) =>
  Math.abs(v) >= 1_000_000 ? `$${(v / 1_000_000).toFixed(2)}M` : Math.abs(v) >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`;
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
/** A typed percentage, as a number. A half-typed "-" or "" is worth nothing yet. */
const pctOf = (text: string) => {
  const n = Number(text);
  return Number.isFinite(n) ? n : 0;
};

/**
 * What one figure is worth in each of the five years at a compounding rise.
 *
 * `firstYear` decides whether the rise lands on Year 1 or on Year 2. Year 1 is
 * what the items bill TODAY, annualised, so the default is Year 2: the first
 * renewal after today. Tick it and every year including the first carries the
 * raise, which is the "we put prices up now" case.
 */
const project = (base: number, pct: number, firstYear: boolean): number[] =>
  YEARS.map((y) => base * Math.pow(1 + pct / 100, firstYear ? y : y - 1));

/** Year by year, added together. Index arithmetic in one place rather than five. */
const addYears = (a: number[], b: number[]) => a.map((v, i) => v + (b[i] ?? 0));
const sum = (a: number[]) => a.reduce((t, v) => t + v, 0);
const yearOf = (a: number[], y: number) => a[y - 1] ?? 0;

/**
 * Reports › Managed Services Forecast — the eight managed-service items on
 * every active contract across the whole client base (routes/
 * reportsManagedServices.ts), with a price rise you set per item and what
 * that is worth over five years.
 *
 * Autotask is read for what the items bill TODAY and nothing else. Every
 * five-year figure on this page is arithmetic done here, so changing a
 * percentage is instant and nothing is ever written back.
 */
export function ManagedServicesReportPage() {
  const [defaultPct, setDefaultPct] = useState('3');
  /* Per item, but only for the items somebody has actually typed into. The
     rest follow the default, so changing the default still moves them. */
  const [perItem, setPerItem] = useState<Record<string, string>>({});
  const [firstYear, setFirstYear] = useState(false);
  const [item, setItem] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'total' | 'now' | 'name' | 'units'>('total');
  /* Its own sort: the app-wide heading sort would split a client from its contract lines. */
  const [open, setOpen] = useState<Set<number>>(new Set());
  const data = useHeldResource<Payload>('/reports/managed-services');
  const d = data.data;

  const pctFor = (name: string) => pctOf(perItem[name] ?? defaultPct);
  const setItemPct = (name: string, text: string) => setPerItem((p) => ({ ...p, [name]: text }));
  const edited = Object.keys(perItem);
  const toggle = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  /** Every client that still has a line after the item and name filters, with
   *  its five years worked out line by line — each line at its own item's rise. */
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out: { id: number; name: string; lines: number; units: number; now: number; years: number[]; total: number; contracts: Contract[] }[] = [];
    for (const c of d?.clients ?? []) {
      if (q && !c.name.toLowerCase().includes(q)) continue;
      const contracts = item ? c.contracts.map((k) => ({ ...k, lines: k.lines.filter((l) => l.item === item) })).filter((k) => k.lines.length > 0) : c.contracts;
      if (contracts.length === 0) continue;
      let years = YEARS.map(() => 0);
      let now = 0;
      let units = 0;
      let lines = 0;
      for (const k of contracts)
        for (const l of k.lines) {
          lines += 1;
          units += l.units ?? 0;
          now += l.annual;
          years = addYears(years, project(l.annual, pctFor(l.item), firstYear));
        }
      out.push({ id: c.id, name: c.name, lines, units, now, years, total: sum(years), contracts });
    }
    return out.sort((a, b) =>
      sortBy === 'name' ? a.name.localeCompare(b.name) : (sortBy === 'now' ? b.now - a.now : sortBy === 'units' ? b.units - a.units : b.total - a.total) || a.name.localeCompare(b.name),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, item, search, sortBy, defaultPct, perItem, firstYear]);

  const totals = useMemo(() => {
    let years = YEARS.map(() => 0);
    let now = 0;
    let units = 0;
    let lines = 0;
    for (const r of rows) {
      now += r.now;
      units += r.units;
      lines += r.lines;
      years = addYears(years, r.years);
    }
    return { years, now, units, lines, total: sum(years) };
  }, [rows]);

  /* The chart: the five years with the rise, against the same five years with
     no rise at all. The gap IS the proposal. */
  const chart = YEARS.map((y) => ({ year: `Year ${y}`, projected: yearOf(totals.years, y), flat: totals.now }));
  const flatTotal = totals.now * 5;
  const gain = totals.total - flatTotal;

  const exportCsv = () => {
    const stamp = fileStamp();
    downloadCsv(`managed_services_forecast_${stamp}.csv`, [
      ['Managed Services Forecast', stamp],
      ['Raise applies from', firstYear ? 'Year 1' : 'Year 2'],
      [],
      ['Item', 'Clients', 'Lines', 'Units', 'Raise %', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Five-year total'],
      ...(d?.byItem ?? [])
        .filter((i) => (!item || i.item === item) && i.lines > 0)
        .map((i) => {
          const p = project(i.annual, pctFor(i.item), firstYear);
          return [i.item, i.clients, i.lines, i.units, pctFor(i.item), ...p.map((v) => Math.round(v * 100) / 100), Math.round(sum(p) * 100) / 100];
        }),
      [],
      ['Client', 'Lines', 'Units', 'Billing today (annual)', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Five-year total'],
      ...rows.map((r) => [r.name, r.lines, r.units, r.now, ...r.years.map((v) => Math.round(v * 100) / 100), Math.round(r.total * 100) / 100]),
      [],
      ['Client', 'Contract', 'Contract ends', 'Item', 'Period', 'Units', 'Unit price', 'Raise %', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
      ...rows.flatMap((r) =>
        r.contracts.flatMap((k) =>
          k.lines.map((l) => [
            r.name,
            k.name,
            k.end ?? '',
            l.item,
            l.period ?? '',
            l.units,
            l.unitPrice,
            pctFor(l.item),
            ...project(l.annual, pctFor(l.item), firstYear).map((v) => Math.round(v * 100) / 100),
          ]),
        ),
      ),
    ]);
  };

  return (
    <>
      <PageHead
        title="Managed Services Forecast"
        lede="The eight managed-service items on every active contract, across the whole client base, and what they are worth over five years at the price rise you set. Read live from Autotask; the percentages are yours and nothing is written back."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Raise, every year" hint="Compounds. Set an item apart below.">
              <div className="row" style={{ alignItems: 'center', gap: 'var(--space-1)' }}>
                <input
                  className="input"
                  type="number"
                  step="0.5"
                  min="-100"
                  max="100"
                  value={defaultPct}
                  onChange={(e) => setDefaultPct(e.target.value)}
                  style={{ width: 96 }}
                  aria-label="Raise for every item, percent a year"
                />
                <span className="muted">% a year</span>
              </div>
            </Field>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {['0', '3', '5', '7', '10'].map((p) => (
                <button key={p} type="button" className="btn btn-ghost btn-sm" onClick={() => setDefaultPct(p)}>
                  {p}%
                </button>
              ))}
            </div>
            <Field label="Item">
              <select className="select" value={item} onChange={(e) => setItem(e.target.value)} style={{ minWidth: 280 }}>
                <option value="">All eight items</option>
                {(d?.items ?? []).map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client name" />
            </Field>
            <Field label="Sort by">
              <select className="select" value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
                <option value="total">Five-year total, highest first</option>
                <option value="now">Billing today, highest first</option>
                <option value="units">Units, most first</option>
                <option value="name">Client name, A–Z</option>
              </select>
            </Field>
          </div>
          <div className="row wrap mt-3" style={{ alignItems: 'center', gap: 'var(--space-3)' }}>
            <label className="checkbox">
              <input type="checkbox" checked={firstYear} onChange={(e) => setFirstYear(e.target.checked)} />
              Put the first raise on Year 1
            </label>
            {edited.length > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPerItem({})}>
                Put {edited.length === 1 ? 'the item set apart' : `all ${edited.length} items set apart`} back on {pctOf(defaultPct)}%
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            Year 1 is what these items bill today, annualised from each line&rsquo;s units, unit price and billing period — not what was invoiced last year.
            The raise compounds from {firstYear ? 'Year 1' : 'Year 2, the first renewal after today'}.
            {d && ` ${num(d.contractsWithItems)} of ${num(d.activeContracts)} active contracts carry one of the items, across ${num(d.clients.length)} clients.`}
          </p>
          {d && !d.unitsKnown && (
            <p className="xs muted mt-1">
              Autotask would not give up the service units, so every line is blank and nothing can be forecast. The API user needs to be able to read contract
              service units.
            </p>
          )}
          {d && (d.missing.length > 0 || d.nearMisses.length > 0) && (
            <p className="xs muted mt-1">
              {d.missing.length > 0 && `Nothing on any active contract matched ${d.missing.join(', ')}. `}
              {d.nearMisses.length > 0 &&
                `${d.nearMisses.length === 1 ? 'One service looks' : `${num(d.nearMisses.length)} services look`} like one of these and is not counted: ${d.nearMisses
                  .slice(0, 6)
                  .map((n) => `${n.name} (${num(n.lines)})`)
                  .join(', ')}${d.nearMisses.length > 6 ? ', and more' : ''}. Rename it in Autotask, or tell me to add it.`}
            </p>
          )}
          {d && (d.periodUnknown > 0 || d.oneTime > 0 || d.unpriced > 0 || d.inactiveLines > 0) && (
            <p className="xs muted mt-1">
              {d.periodUnknown > 0 && `${num(d.periodUnknown)} lines have no billing period in Autotask and are counted as monthly. `}
              {d.oneTime > 0 && `${num(d.oneTime)} are one-time and bill nothing a year. `}
              {d.unpriced > 0 && `${num(d.unpriced)} have no units or no price and are worth nothing here. `}
              {d.inactiveLines > 0 && `${num(d.inactiveLines)} sit under inactive companies and are left out.`}
            </p>
          )}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading contracts and services from Autotask" />}

      {d && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Billing today" value={money0(totals.now)} foot={`a year · ${money0(totals.now / 12)} a month`} />
            <Stat
              label="Year 5"
              value={money0(yearOf(totals.years, 5))}
              foot={totals.now > 0 ? `${(((yearOf(totals.years, 5) - totals.now) / totals.now) * 100).toFixed(1)}% above today` : 'nothing billing today'}
            />
            <Stat label="Five years" value={money0(totals.total)} foot={`${num(totals.lines)} lines · ${num(totals.units)} units`} />
            <Stat label="The raise is worth" value={money0(gain)} foot="over five years, above standing still" flagged={gain > 0} />
          </div>

          <ChartCard
            title="Five years, with the raise and without"
            subtitle={`Every ${item || 'managed-service'} line across ${rows.length === 1 ? 'this client' : `${num(rows.length)} clients`}, each at its own item's rise, against the same book standing still at today's prices.`}
            table={{
              columns: [
                { key: 'year', label: 'Year' },
                { key: 'flat', label: 'No raise', align: 'right', render: (r: { flat: number }) => money(r.flat) },
                { key: 'projected', label: 'With the raise', align: 'right', render: (r: { projected: number }) => money(r.projected) },
                { key: 'gap', label: 'Difference', align: 'right', render: (r: { projected: number; flat: number }) => money(r.projected - r.flat) },
              ],
              rows: chart,
              rowKey: (r) => r.year,
            }}
          >
            <LineChart
              data={chart}
              xKey="year"
              series={[
                { key: 'projected', label: 'With the raise', color: 'var(--series-1)' },
                { key: 'flat', label: 'No raise', color: 'var(--series-4)' },
              ]}
              yFormat={moneyShort}
              zeroBased
              height={280}
              emptyMessage="No managed-service lines on any active contract."
            />
          </ChartCard>

          <section className="card mt-4">
            <div className="card-body">
              <h2 className="h4">The eight items</h2>
              <p className="xs muted">
                What each item bills across the whole client base today, and what it is worth in Year 5. Change one item&rsquo;s percentage to price it apart
                from the rest; the table below follows line by line.
              </p>
            </div>
            <div className="table-wrap">
              <table className="table" data-nosort>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Clients</th>
                    <th className="num">Units</th>
                    <th className="num">Unit price</th>
                    <th className="num">Billing today</th>
                    <th className="num">Raise</th>
                    <th className="num">Year 5</th>
                    <th className="num">Five years</th>
                  </tr>
                </thead>
                <tbody>
                  {d.byItem.map((i) => {
                    const p = project(i.annual, pctFor(i.item), firstYear);
                    const own = perItem[i.item];
                    const apart = own !== undefined && pctOf(own) !== pctOf(defaultPct);
                    return (
                      <tr key={i.item} className={i.lines === 0 ? 'muted' : undefined}>
                        <td>
                          {i.item}
                          {i.lines === 0 && <span className="xs muted"> · nothing on any active contract</span>}
                        </td>
                        <td className="num">{num(i.clients)}</td>
                        <td className="num">{num(i.units)}</td>
                        <td className="num xs">
                          {i.minPrice === null
                            ? '—'
                            : i.minPrice === i.maxPrice
                              ? money(i.minPrice)
                              : `${money(i.minPrice)} – ${money(i.maxPrice ?? i.minPrice)}`}
                        </td>
                        <td className="num">{money(i.annual)}</td>
                        <td className="num">
                          <input
                            className="input"
                            type="number"
                            step="0.5"
                            min="-100"
                            max="100"
                            value={perItem[i.item] ?? defaultPct}
                            onChange={(e) => setItemPct(i.item, e.target.value)}
                            style={{ width: 86, textAlign: 'right' }}
                            aria-label={`Raise for ${i.item}, percent a year`}
                          />
                          {apart && <span className="xs muted"> set apart</span>}
                        </td>
                        <td className="num">{money(yearOf(p, 5))}</td>
                        <td className="num strong">{money(sum(p))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card mt-4">
            <div className="table-wrap">
              <table className="table" data-nosort>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="num">Units</th>
                    <th className="num">Today</th>
                    {YEARS.map((y) => (
                      <th key={y} className="num">
                        Year {y}
                      </th>
                    ))}
                    <th className="num">Five years</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="muted small">
                        No active contract carries {item || 'any of the eight items'}
                        {search.trim() ? ' for a client of that name' : ''}.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => {
                    const isOpen = open.has(r.id);
                    return (
                      <Fragment key={r.id}>
                        <tr className={isOpen ? 'is-open' : undefined}>
                          <td>
                            <button type="button" className="link-button" onClick={() => toggle(r.id)} aria-expanded={isOpen}>
                              {isOpen ? '▾' : '▸'} {r.name}
                            </button>
                            <span className="xs muted">
                              {' '}
                              · {num(r.lines)} {r.lines === 1 ? 'line' : 'lines'} on {num(r.contracts.length)}{' '}
                              {r.contracts.length === 1 ? 'contract' : 'contracts'}
                            </span>
                          </td>
                          <td className="num">{num(r.units)}</td>
                          <td className="num">{money(r.now)}</td>
                          {r.years.map((v, i) => (
                            <td key={i} className="num">
                              {money(v)}
                            </td>
                          ))}
                          <td className="num strong">{money(r.total)}</td>
                        </tr>
                        {isOpen &&
                          r.contracts.map((k) => (
                            <Fragment key={k.id}>
                              <tr className="inv-line">
                                <td className="xs">
                                  {k.name}
                                  {k.number && <span className="mono"> · {k.number}</span>}
                                  <span className="muted">
                                    {' '}
                                    · {k.type ?? 'contract'} · {k.end ? `ends ${day(k.end)}` : 'no end date'}
                                  </span>
                                </td>
                                <td className="num xs">{num(k.lines.reduce((t, l) => t + (l.units ?? 0), 0))}</td>
                                <td className="num xs">{money(k.lines.reduce((t, l) => t + l.annual, 0))}</td>
                                {YEARS.map((y) => (
                                  <td key={y} className="num xs">
                                    {money(k.lines.reduce((t, l) => t + yearOf(project(l.annual, pctFor(l.item), firstYear), y), 0))}
                                  </td>
                                ))}
                                <td className="num xs strong">{money(k.lines.reduce((t, l) => t + sum(project(l.annual, pctFor(l.item), firstYear)), 0))}</td>
                              </tr>
                              {k.lines.map((l) => {
                                const p = project(l.annual, pctFor(l.item), firstYear);
                                return (
                                  <tr key={l.id} className="inv-line">
                                    <td className="xs muted" style={{ paddingLeft: 'var(--space-5)' }}>
                                      {l.item} · {l.units === null ? 'units unknown' : `${num(l.units)} × ${money(l.unitPrice ?? 0)}`}
                                      {l.period ? ` ${l.period.toLowerCase()}` : ''} · {pctFor(l.item)}%
                                    </td>
                                    <td className="num xs muted">{l.units === null ? '—' : num(l.units)}</td>
                                    <td className="num xs muted">{money(l.annual)}</td>
                                    {p.map((v, i) => (
                                      <td key={i} className="num xs muted">
                                        {money(v)}
                                      </td>
                                    ))}
                                    <td className="num xs muted">{money(sum(p))}</td>
                                  </tr>
                                );
                              })}
                            </Fragment>
                          ))}
                      </Fragment>
                    );
                  })}
                  {rows.length > 1 && (
                    <tr className="cr-total">
                      <td>
                        {num(rows.length)} clients · {num(totals.lines)} lines
                      </td>
                      <td className="num">{num(totals.units)}</td>
                      <td className="num">{money(totals.now)}</td>
                      {totals.years.map((v, i) => (
                        <td key={i} className="num">
                          {money(v)}
                        </td>
                      ))}
                      <td className="num strong">{money(totals.total)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
